import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Truck, ClipboardList, PackageOpen, ArrowLeft, ArrowRight,
  CheckCircle2, CalendarCheck2, AlertTriangle, Mail, MailX,
} from "lucide-react";

const ROLE_META = {
  delivery_driver: { label: "Delivery Driver", icon: Truck },
  delivery_coordinator: { label: "Delivery Coordinator", icon: ClipboardList },
  pickup_driver: { label: "Pick Up Driver", icon: PackageOpen },
};

function formatSaturdayShort(iso) {
  const d = new Date(iso + "T12:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

export default function AssignSaturdaysDialog({ member, open, onClose, onDone }) {
  const [board, setBoard] = useState(null);
  const [step, setStep] = useState("role"); // role | dates | result
  const [role, setRole] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep("role"); setRole(null); setSelected(new Set()); setNotify(true); setResult(null);
    api.get("/public/board")
      .then((r) => setBoard(r.data))
      .catch(() => toast.error("Couldn't load upcoming Saturdays."));
  }, [open]);

  const mySignupShiftIds = useMemo(() => {
    if (!board?.days || !member?.email) return new Set();
    const out = new Set();
    const email = member.email.toLowerCase();
    for (const d of board.days) {
      for (const s of d.shifts) {
        for (const r of Object.values(s.roles)) {
          for (const p of (r.people || [])) {
            if ((p.email || "").toLowerCase() === email) out.add(s.id);
          }
        }
      }
    }
    return out;
  }, [board, member]);

  const candidates = useMemo(() => {
    if (!board?.days || !role) return [];
    const out = [];
    for (const d of board.days) {
      if (new Date(d.date + "T12:00:00").getDay() !== 6) continue;
      for (const s of d.shifts) {
        if (mySignupShiftIds.has(s.id)) continue;
        if ((s.roles?.[role]?.open || 0) <= 0) continue;
        out.push({ id: s.id, date: s.date, start_time: s.start_time, end_time: s.end_time,
                    route_name: s.route?.name || "" });
      }
    }
    return out;
  }, [board, role, mySignupShiftIds]);

  const roleCounts = useMemo(() => {
    const out = { delivery_driver: 0, delivery_coordinator: 0, pickup_driver: 0 };
    if (!board?.days) return out;
    for (const d of board.days) {
      if (new Date(d.date + "T12:00:00").getDay() !== 6) continue;
      for (const s of d.shifts) {
        if (mySignupShiftIds.has(s.id)) continue;
        for (const k of Object.keys(out)) if ((s.roles?.[k]?.open || 0) > 0) out[k] += 1;
      }
    }
    return out;
  }, [board, mySignupShiftIds]);

  const toggleDate = (id) => setSelected((prev) => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const allChecked = candidates.length > 0 && candidates.every((c) => selected.has(c.id));
  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(candidates.map((c) => c.id)));
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/signups/bulk-assign", {
        member_id: member.id,
        role,
        shift_ids: Array.from(selected),
        notify,
      });
      setResult(data);
      setStep("result");
      onDone?.();
      if (data.claimed_count > 0) toast.success(`Assigned ${data.claimed_count} Saturday${data.claimed_count === 1 ? "" : "s"}.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const titles = {
    role: `Assign ${member?.name || "member"} · Step 1 of 2 · Pick a role`,
    dates: `Assign ${member?.name || "member"} · Step 2 of 2 · Pick Saturdays`,
    result: "All set!",
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="assign-saturdays-dialog" className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Assign Saturdays</DialogTitle>
          <DialogDescription>{titles[step]}</DialogDescription>
        </DialogHeader>

        {step === "role" && (
          <div className="space-y-3 mt-2">
            <p className="text-sm text-slate-600">
              Signing up <b>{member?.name}</b> · <span className="text-slate-500">{member?.email}</span>.
              Pick the role you want to assign them to.
            </p>
            <div className="grid gap-2">
              {Object.entries(ROLE_META).map(([key, meta]) => {
                const Icon = meta.icon;
                const n = roleCounts[key];
                const disabled = n === 0;
                return (
                  <button
                    key={key}
                    data-testid={`assign-role-${key}`}
                    disabled={disabled}
                    onClick={() => { setRole(key); setStep("dates"); }}
                    className={`text-left rounded-xl border-2 p-4 flex items-center gap-4 transition-all focus-ring ${
                      disabled ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"
                               : "border-slate-200 hover:border-brand-blue hover:bg-brand-blue/5"
                    }`}
                  >
                    <div className={`p-3 rounded-lg shrink-0 ${disabled ? "bg-slate-200 text-slate-400" : "bg-brand-blue/10 text-brand-blue"}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-semibold text-slate-900">{meta.label}</div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className={`text-lg font-bold leading-none ${disabled ? "text-slate-400" : "text-brand-blue"}`}>{n}</div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mt-1">open dates</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === "dates" && role && (
          <div className="space-y-3 mt-2">
            <div className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox data-testid="assign-select-all" checked={allChecked} onCheckedChange={toggleAll} />
                <span className="text-sm font-semibold text-slate-700">Select all ({candidates.length})</span>
              </label>
              <span className="text-xs font-bold uppercase tracking-wider text-brand-blue">{selected.size} selected</span>
            </div>
            <div className="max-h-[45vh] overflow-y-auto -mx-1 px-1 space-y-2">
              {candidates.map((c) => {
                const checked = selected.has(c.id);
                return (
                  <label key={c.id} data-testid={`assign-date-row-${c.id}`}
                    className={`flex items-center gap-3 rounded-lg border-2 px-3 py-2.5 cursor-pointer ${
                      checked ? "border-brand-blue bg-brand-blue/5" : "border-slate-200 hover:border-slate-300"
                    }`}>
                    <Checkbox data-testid={`assign-date-cb-${c.id}`} checked={checked} onCheckedChange={() => toggleDate(c.id)} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 text-sm">{formatSaturdayShort(c.date)}</div>
                      <div className="text-xs text-slate-500 truncate">{c.route_name} · {c.start_time}–{c.end_time}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 cursor-pointer">
              <Checkbox data-testid="assign-notify" checked={notify} onCheckedChange={(v) => setNotify(!!v)} className="mt-0.5" />
              <div className="text-sm text-slate-700">
                <p className="font-semibold flex items-center gap-1.5">
                  {notify ? <Mail className="w-3.5 h-3.5" /> : <MailX className="w-3.5 h-3.5" />}
                  Send them a confirmation email
                </p>
                <p className="text-xs text-slate-500 mt-0.5">Uncheck if you&apos;re bulk-importing history and don&apos;t want to notify.</p>
              </div>
            </label>
            <div className="flex items-center justify-between pt-2 gap-2">
              <Button type="button" variant="outline" onClick={() => setStep("role")} className="min-h-[44px]">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Change role
              </Button>
              <Button type="button" onClick={submit} disabled={busy || selected.size === 0}
                className="min-h-[44px] bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold"
                data-testid="assign-submit">
                {busy ? "Assigning…" : `Assign ${selected.size > 0 ? selected.size : ""}`} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>
        )}

        {step === "result" && result && (
          <div className="space-y-4 mt-2" data-testid="assign-result">
            <div className="text-center">
              <div className="mx-auto h-14 w-14 rounded-full bg-brand-green/15 text-brand-green-dark flex items-center justify-center mb-2">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <p className="font-heading text-2xl text-slate-900 font-semibold">
                {result.claimed_count} Saturday{result.claimed_count === 1 ? "" : "s"} assigned
              </p>
              {result.notified && <p className="text-xs text-slate-500 mt-1">Confirmation email sent to {result.member.email}.</p>}
              {!result.notified && result.claimed_count > 0 && <p className="text-xs text-slate-500 mt-1">Email skipped.</p>}
            </div>
            {result.skipped_count > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> Skipped
                </p>
                {result.skipped.map((s, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs">
                    <span className="font-semibold text-slate-700">{s.date ? formatSaturdayShort(s.date) : "Unknown"}</span>
                    <span className="text-amber-800">{s.message}</span>
                  </div>
                ))}
              </div>
            )}
            <Button type="button" onClick={onClose} data-testid="assign-result-done"
              className="w-full min-h-[44px] bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
